import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormControl, UntypedFormGroup, Validators, UntypedFormBuilder, UntypedFormArray } from '@angular/forms';
import { IssuersService } from '../../services/issuers.service';
import { Issuer } from '../../model/issuer';
import { DcAlertService } from '../../../ui-kit/dc-alert/dc-alert.service';
import { IssuerUserRoles } from '../../model/issuer-sales-contact';
import { PlatformLocation } from '@angular/common';
import { TransferService } from '../../services/transfer.service';

interface DropdownItem {
  id: string;
  displayValue: string;
}

@Component({
    selector: 'deltacapita-create-issuer-contact',
    templateUrl: './create-issuer-contact.component.html',
    styleUrls: ['./create-issuer-contact.component.scss'],
    standalone: false
})
export class CreateIssuerContactComponent implements OnInit {
  @Input() modal: NgbModalRef;

  loading = true;
  issuerid: string;
  isIssuerSet = false;
  allRegions: string[] = [];
  currentTaggedRegions: string[] = [];
  regionsToBeRemoved: string[] = [];
  isRegionDisabled: boolean = false;
  selectedRegions: string[];
  roleToBeEdited: string;
  allDesks: string[] = [];
  currentTaggedDesks: string[] = [];
  desksToBeRemoved: string[] = [];
  isDeskDisabled: boolean = false;
  selectedDesks: string[];

  createIssuerContactForm: UntypedFormGroup;

  @ViewChild('loadingSpinner', { static: true }) loadingSpinner: TemplateRef<any>;

  issuersDropdownData: DropdownItem[];
  rolesDropdownData: DropdownItem[] = [
    { id: IssuerUserRoles.SALES, displayValue: 'Sales' },
    { id: IssuerUserRoles.MANAGER, displayValue: 'Manager' },
    { id: IssuerUserRoles.COMPLIANCE, displayValue: 'Compliance' },
    { id: IssuerUserRoles.LEGAL_APPROVER, displayValue: 'Legal Approver' },
  ];

  constructor(
    private issuersService: IssuersService,
    private fb: UntypedFormBuilder,
    private dcAlertService: DcAlertService,
    private transferService: TransferService,
    location: PlatformLocation
  ) {
    location.onPopState((event) => {
      if (this.modal !== undefined) {
        this.modal.close();
      }
    });
  }

  private salesregion: UntypedFormArray;
  private salesdesk: UntypedFormArray;

  async ngOnInit(): Promise<void> {
    this.createIssuerContactForm = this.fb.group({
      issuerid: new UntypedFormControl(null, Validators.required),
      name: new UntypedFormControl(null, Validators.required),
      email: new UntypedFormControl(null, [Validators.required, Validators.email]),
      role: new UntypedFormControl(null, Validators.required),
    });

    if (this.transferService.isClicked) {
      this.issuerid = this.transferService.getData() || '';
      if (this.issuerid) {
        this.createIssuerContactForm.patchValue({ issuerid: this.issuerid });
        this.getDeskAndRegion(this.issuerid);
        this.isIssuerSet = true;
      }
      this.transferService.isClicked = false;
    }

    this.issuersService.getAllIssuers().subscribe((issuers: Issuer[]) => {
      this.issuersDropdownData = this.makeDropdownItems(issuers, 'id', 'name').filter(
        (x) => x.id !== '00000000-0000-0000-0000-000000000000' // exclude admin account
      );
      this.loading = false;
    });

    this.createIssuerContactForm.valueChanges.subscribe(async (values) => {
      if (values.role) {
        this.roleToBeEdited = values.role;
        if (this.roleToBeEdited === 'Compliance') {
          this.isRegionDisabled = true;
          this.isDeskDisabled = true;
        } else if (this.roleToBeEdited === 'Manager' || this.roleToBeEdited === 'Legal Approver') {
          this.isDeskDisabled = true;
          this.isRegionDisabled = false;
          this.isDeskDisabled = false;
        } else if (this.roleToBeEdited === 'Sales') {
          this.isRegionDisabled = false;
          this.isDeskDisabled = false;
        }
      }

      if (values.issuerid) {
        this.issuerid = this.createIssuerContactForm.getRawValue().issuerid;
        this.getDeskAndRegion(this.issuerid);

        // set how roles can dictate regions behaviour
        if (values.role === 'Compliance' || values.role === 'Manager' || values.role === 'Legal Approver') {
          // reset to all regions
          this.isDeskDisabled = true;
          this.currentTaggedDesks = this.allDesks;
          this.desksToBeRemoved = [];
          // set how roles can dictate regions behaviour
          if (values.role === 'Compliance') {
            // reset to all regions
            this.isRegionDisabled = true;
            this.currentTaggedRegions = this.allRegions;
            this.regionsToBeRemoved = [];
          } else {
            this.isRegionDisabled = false;
            this.currentTaggedRegions = this.currentTaggedRegions.filter((x) => !this.regionsToBeRemoved.includes(x));
            this.regionsToBeRemoved = [];
          }
        } else {
          this.isRegionDisabled = false;
          this.isDeskDisabled = false;
          // excluding currently un-ticked regions
          this.currentTaggedRegions = this.currentTaggedRegions.filter((x) => {
            return !this.regionsToBeRemoved.includes(x);
          });
          this.regionsToBeRemoved = [];
          // excluding currently un-ticked desks
          this.currentTaggedDesks = this.currentTaggedDesks.filter((x) => {
            return !this.desksToBeRemoved.includes(x);
          });
          this.desksToBeRemoved = [];
        }
      }
      // console.log(this.createIssuerContactForm.value);
      // console.log(this.currentTaggedDesks, this.currentTaggedRegions, 'to be selected');
    });
  }

  getDeskAndRegion(issuerID) {
    this.issuersService.getSalesDesksByIssuer(issuerID).subscribe((data: any) => {
      this.allDesks = data.salesdesks;
    });
    this.issuersService.getSalesRegionsByIssuer(issuerID).subscribe((data: any) => {
      this.allRegions = data.salesregion;
    });
  }

  validateForm() {
    return this.createIssuerContactForm.valid && this.getCheckedDesks() && this.getCheckedRegions();
  }

  getCheckedDesks() {
    const checkedCount: number = this.currentTaggedDesks.length;
    return checkedCount > 0 ? true : false;
  }

  getCheckedRegions() {
    const checkedCount: number = this.currentTaggedRegions.length;
    return checkedCount > 0 ? true : false;
  }

  onDeskCheckboxChange(e) {
    if (e.target.checked && !this.currentTaggedDesks.includes(e.target.value)) {
      // store all ticked desks into form control
      this.currentTaggedDesks.push(e.target.value);
      if (this.desksToBeRemoved.includes(e.target.value)) {
        this.desksToBeRemoved = this.desksToBeRemoved.filter((x) => x !== e.target.value);
      }
    } else if (!e.target.checked) {
      // store all desks that have been un-ticked
      !this.desksToBeRemoved.includes(e.target.value) && this.desksToBeRemoved.push(e.target.value);
    }
  }

  onRegionCheckboxChange(e) {
    if (e.target.checked && !this.currentTaggedRegions.includes(e.target.value)) {
      // store all ticked regions into form control
      this.currentTaggedRegions.push(e.target.value);
      if (this.regionsToBeRemoved.includes(e.target.value)) {
        this.regionsToBeRemoved = this.regionsToBeRemoved.filter((x) => x !== e.target.value);
      }
    } else if (!e.target.checked) {
      // store all regions that have been un-ticked
      !this.regionsToBeRemoved.includes(e.target.value) && this.regionsToBeRemoved.push(e.target.value);
    }
  }

  async createNewIssuerSalesContact(): Promise<void> {
    const { id, issuerid, name, email, role } = this.createIssuerContactForm.value;

    this.currentTaggedDesks = this.currentTaggedDesks.filter((x) => !this.desksToBeRemoved.includes(x));
    this.currentTaggedRegions = this.currentTaggedRegions.filter((x) => !this.regionsToBeRemoved.includes(x));

    const newIssuerContact = {
      id: id,
      issuerid: issuerid,
      name: name,
      email: email,
      role: role,
      salesdesk: this.currentTaggedDesks,
      salesregion: this.currentTaggedRegions,
    };
    console.log(newIssuerContact);

    this.issuersService.addIssuerSalesContact(newIssuerContact, {
      next: this.callback,
      error: this.errorCallback,
    });

    this.modal.dismiss();
  }

  callback = (obj) => {
    this.issuersService.refreshContactsSubject.next();
    this.loading = false;
    this.transferService.notifyOther({ toRefresh: true, keysToRemove: ['issuerContacts'] });
    this.dcAlertService.showAlert({
      type: 'success',
      message: `Successfully added issuer contact.)`,
    });
  };
  errorCallback = (e) => {
    console.log(e);
    this.dcAlertService.showAlert({
      type: 'danger',
      message: 'Error creating issuer sales contact!',
    });
  };

  makeDropdownItems(data, idField, displayValueField): DropdownItem[] {
    return data.map((dataItem) => {
      return {
        id: dataItem[idField],
        displayValue: Array.isArray(displayValueField)
          ? `${dataItem[displayValueField[0]]} (${dataItem[displayValueField[1]]})`
          : dataItem[displayValueField],
      };
    });
  }
}
